/**
 * BulkAddOrgs — surface 7 (bulk-add affordance for organizations)
 * (Block 6.E / ADR-017 §"Bulk-adding 12 organizations").
 *
 * Two stages controlled by local state:
 *   Stage 1 — Paste textarea + Parse-Preview button (BulkAddPasteForm).
 *   Stage 2 — 3-block preview + Confirm gate (BulkAddPreviewPane).
 *
 * Steward-gated per Phase 1 floor (design plan §1.4). Server enforces
 * requireDataStewardRole independently; UI gate + page render avoid
 * surfacing affordances that always 403.
 */

import { useState, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { canAddLookup } from '@/lib/lookups';
import { bulkAddApi } from '@/lib/bulkAdd';
import { ApiError } from '@/lib/api';
import { errorCodeOf, pageWriteText, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import type {
  BulkAddCommitResponse,
  BulkAddPreviewResponse,
  BulkAddPendingLookupAdd,
  BulkAddCommitRow,
} from '@/lib/types';
import { BulkAddPasteForm } from '@/components/BulkAddPasteForm';
import { BulkAddPreviewPane } from '@/components/BulkAddPreviewPane';

type Stage = 'paste' | 'preview' | 'success';

interface CommitInput {
  rows: BulkAddCommitRow[];
  pending_lookup_adds: BulkAddPendingLookupAdd[];
  approved_lookup_seeding: boolean;
}

export function BulkAddOrgs(): ReactNode {
  const { tenantSlug: routeTenantSlug } = useParams<{ tenantSlug: string }>();
  const { whoami } = useTenant();

  const tenantSlug = routeTenantSlug ?? '';
  const isSteward = canAddLookup(whoami);
  // The page turns everyone else away, so only a steward reads the contact.
  const contact = useSupportContact({ enabled: isSteward });

  const [stage, setStage] = useState<Stage>('paste');
  const [paste, setPaste] = useState<string>('');
  const [preview, setPreview] = useState<BulkAddPreviewResponse | null>(null);
  const [commitResult, setCommitResult] = useState<BulkAddCommitResponse | null>(null);

  const previewMutation = useMutation({
    mutationFn: async (pasteText: string) => bulkAddApi.preview(tenantSlug, pasteText),
    onSuccess: (data) => {
      setPreview(data);
      setStage('preview');
    },
  });

  const commitMutation = useMutation({
    mutationFn: async (input: CommitInput) => bulkAddApi.commit(tenantSlug, input),
    onSuccess: (data) => {
      setCommitResult(data);
      setStage('success');
    },
  });

  if (!isSteward) {
    return (
      <div className="px-3 py-6">
        <h2 className="font-display text-lg text-sage-800 mb-2">Access denied</h2>
        <div role="alert" className="my-3 p-3 border border-pepper/30 bg-pepper-soft rounded-base text-sm text-ink">
          Bulk-add is restricted to data stewards.
        </div>
      </div>
    );
  }

  function handleParse(pasteText: string): void {
    setPaste(pasteText);
    previewMutation.mutate(pasteText);
  }

  function handleEditPaste(): void {
    setStage('paste');
  }

  function handleCommit(input: CommitInput): void {
    commitMutation.mutate(input);
  }

  return (
    <div className="px-3 py-4 max-w-3xl mx-auto">
      {/* The way back is the console's own page-level control (one back
          control per page); this view carries none of its own. */}
      <h2 className="font-display text-lg text-sage-800 my-3">Add organizations</h2>

      {stage === 'paste' && (
        <BulkAddPasteForm
          initialPaste={paste}
          busy={previewMutation.isPending}
          errorMessage={
            previewMutation.error ? bulkAddRefusalText(previewMutation.error, contact) : null
          }
          onParse={handleParse}
        />
      )}

      {stage === 'preview' && preview && (
        <BulkAddPreviewPane
          rows={preview.rows}
          pendingLookupAdds={preview.pending_lookup_adds}
          busy={commitMutation.isPending}
          errorMessage={
            commitMutation.error ? formatCommitError(commitMutation.error, contact) : null
          }
          onEditPaste={handleEditPaste}
          onCommit={handleCommit}
        />
      )}

      {stage === 'success' && commitResult && (
        <div className="space-y-3">
          <div className="border border-green-300 bg-green-50 rounded-[12px] p-3 text-sm flex items-start gap-1.5">
            <Check size={16} aria-hidden="true" className="text-green-700 shrink-0 mt-0.5" />
            <span>
            Created {commitResult.created_org_ids.length} organization
            {commitResult.created_org_ids.length === 1 ? '' : 's'}.
            {commitResult.skipped_as_duplicate > 0 && (
              <> {commitResult.skipped_as_duplicate} skipped as duplicate.</>
            )}
            {commitResult.skipped_with_error > 0 && (
              <> {commitResult.skipped_with_error} skipped with errors.</>
            )}
            {(commitResult.added_subcategories > 0 ||
              commitResult.added_service_areas > 0) && (
              <>
                {' '}
                {commitResult.added_subcategories} new subcategor
                {commitResult.added_subcategories === 1 ? 'y' : 'ies'} +
                {' '}{commitResult.added_service_areas} new service area
                {commitResult.added_service_areas === 1 ? '' : 's'} added.
              </>
            )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function formatCommitError(err: unknown, contact: SupportContact): string {
  if (errorCodeOf(err) === 'lookup_seeding_not_approved') {
    return 'Tick the approval box to add the new categories, then press Approve & Create.';
  }
  return bulkAddRefusalText(err, contact);
}

/** The row a refusal names, as a person counts it: the body's 0-based
 *  row_index plus one; null when the body names no row. */
function rowOf(err: unknown): number | null {
  if (!(err instanceof ApiError) || !err.body || typeof err.body !== 'object') return null;
  const index = (err.body as { row_index?: unknown }).row_index;
  return typeof index === 'number' && Number.isInteger(index) && index >= 0 ? index + 1 : null;
}

/** One sentence per refusal code the bulk-add routes answer with. `{row}`
 *  is the row the refusal names. */
const BULK_ADD_REFUSALS: Readonly<Record<string, string>> = {
  empty_paste: "There's nothing to read yet. Paste the rows first.",
  paste_too_large: 'That paste is too long to read in one go. Split it into smaller pastes.',
  invalid_kind: "Row {row} has a kind that isn't on the list. Fix it in the paste and read it again.",
  category_not_found:
    "Row {row} names a service category that isn't on the list. Fix it in the paste and read it again.",
  bulk_add_row_validation:
    'Row {row} is missing its name, kind or category. Add it to the paste and read it again.',
};

/** A refused read or commit as one sentence: by code first, then the
 *  house save sentence by status. */
function bulkAddRefusalText(err: unknown, contact: SupportContact): string {
  const code = errorCodeOf(err);
  const sentence = code === null ? undefined : BULK_ADD_REFUSALS[code];
  if (sentence === undefined) return pageWriteText(err, contact);
  if (!sentence.includes('{row}')) return sentence;
  const row = rowOf(err);
  return row === null ? pageWriteText(err, contact) : sentence.replace('{row}', String(row));
}
